'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/routing';
import { toast } from 'sonner';
import { Camera, Loader2, QrCode, ScanLine, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useQrCameraScanner } from '@/hooks/use-qr-camera-scanner';
import { useRouteAccess } from '@/hooks/use-route-access';
import { SCANNER_ENTITY_ROUTES } from '../constants';
import { useQrResolveMutation } from '../services/global-scanner.mutations';

const CAMERA_CONTAINER_ID = 'global-scanner-camera';

interface GlobalScannerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalScannerModal({
  open,
  onOpenChange
}: GlobalScannerModalProps) {
  const t = useTranslations('globalScanner');
  const tg = useTranslations('auth.guard');
  const router = useRouter();
  const canAccess = useRouteAccess();
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [cameraMode, setCameraMode] = useState(true);

  const { mutate: resolveQr, isPending } = useQrResolveMutation();

  const handleResolve = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      resolveQr(trimmed, {
        onSuccess: (result) => {
          // One scanner resolves codes across four modules, so the code in the
          // user's hand routinely belongs to a screen their role cannot open.
          const target = SCANNER_ENTITY_ROUTES[result.entity_type](
            result.entity_id,
            result.label
          );
          if (!canAccess(target)) {
            toast.error(tg('noPermission'));
            setCode('');
            inputRef.current?.focus();
            return;
          }
          onOpenChange(false);
          router.push(target);
        },
        onError: () => {
          toast.error(t('notFound'));
          setCode('');
          inputRef.current?.focus();
        }
      });
    },
    [resolveQr, onOpenChange, router, t, tg, canAccess]
  );

  const { scannerReady, scannerError, startScanner, stopScanner } =
    useQrCameraScanner({
      containerId: CAMERA_CONTAINER_ID,
      stage: 'global-scanner',
      onScanValue: handleResolve,
      t
    });

  useEffect(() => {
    if (open && !cameraMode) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, cameraMode]);

  useEffect(() => {
    if (!open) {
      stopScanner();
      return;
    }
    if (cameraMode) {
      startScanner();
    } else {
      stopScanner();
    }
  }, [open, cameraMode, startScanner, stopScanner]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setCode('');
        setCameraMode(true);
      }, 300);
    }
  }, [open]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleResolve(code);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[460px]'>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        {!cameraMode ? (
          <>
            <div className='grid gap-4 py-2'>
              <div className='relative'>
                <QrCode className='text-muted-foreground absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2' />
                <Input
                  ref={inputRef}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('placeholder')}
                  disabled={isPending}
                  className='ps-9'
                  autoComplete='off'
                />
              </div>

              <Button
                variant='outline'
                className='w-full'
                onClick={() => setCameraMode(true)}
                disabled={isPending}
              >
                <Camera className='me-2 h-4 w-4' />
                {t('backToCamera')}
              </Button>
            </div>

            <DialogFooter>
              <Button
                variant='ghost'
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={() => handleResolve(code)}
                disabled={!code.trim() || isPending}
              >
                {isPending ? (
                  <Loader2 className='me-2 h-4 w-4 animate-spin' />
                ) : (
                  <ScanLine className='me-2 h-4 w-4' />
                )}
                {isPending ? t('resolving') : t('submit')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className='grid gap-3 py-2'>
              <div
                id={CAMERA_CONTAINER_ID}
                className='bg-muted aspect-square w-full overflow-hidden rounded-lg border'
              />
              {scannerError && (
                <p className='text-destructive text-sm'>{scannerError}</p>
              )}
              {scannerReady && !scannerError && (
                <p className='text-muted-foreground text-center text-sm'>
                  {t('pointCameraAtQr')}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button variant='outline' onClick={() => setCameraMode(false)}>
                <X className='me-2 h-4 w-4' />
                {t('typeManually')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
